<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PROJECT_NOTICE_ICON_OPTIONS, getProjectNoticeIconComponent } from '~/lib/projectNotice';
import type { Project, ProjectNoticeIcon } from '~/types/models';
import { toast } from 'vue-sonner';

const props = defineProps<{
  open: boolean;
  project?: Project | null;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'saved'): void;
}>();

const projectStore = useProjectStore();

const saving = ref(false);
const message = ref('');
const color = ref('#f59e0b');
const icon = ref<ProjectNoticeIcon>('alert-triangle');

watch(
  () => [props.open, props.project?.id] as const,
  ([open]) => {
    if (!open) return;
    message.value = String(props.project?.notice?.message || '').trim();
    color.value = props.project?.notice?.color || '#f59e0b';
    icon.value = props.project?.notice?.icon || 'alert-triangle';
  },
  { immediate: true },
);

function closeDialog() {
  emit('update:open', false);
}

async function saveNotice() {
  if (!props.project?.id) return;

  saving.value = true;
  try {
    await projectStore.updateProject(props.project.id, {
      notice: message.value.trim()
        ? {
            message: message.value.trim(),
            color: color.value || '#f59e0b',
            icon: icon.value || 'alert-triangle',
          }
        : undefined,
    });

    toast.success(message.value.trim() ? 'Project notice updated.' : 'Project notice cleared.');
    emit('saved');
    closeDialog();
  } catch (error: any) {
    toast.error(error?.message || 'Failed to update project notice.');
  } finally {
    saving.value = false;
  }
}

async function clearNotice() {
  message.value = '';
  await saveNotice();
}
</script>

<template>
  <Sheet :open="open" @update:open="(value) => emit('update:open', value)">
    <SheetContent class="w-full overflow-y-auto sm:max-w-xl">
      <SheetHeader class="text-left">
        <SheetTitle>Change project notice</SheetTitle>
        <SheetDescription>
          Add a short project notice with a color and icon so the state is obvious at a glance.
        </SheetDescription>
      </SheetHeader>

      <div class="space-y-4">
        <div class="rounded-xl border border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
          <p class="font-medium text-foreground">{{ project?.name || 'Project' }}</p>
          <p class="mt-1">This notice appears on the project card and the project page.</p>
        </div>

        <div class="space-y-2">
          <Label for="project-notice-message">Notice message</Label>
          <Input
            id="project-notice-message"
            v-model="message"
            placeholder="Explain something important, like why the project is on hold."
          />
        </div>

        <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <div class="space-y-2">
            <Label>Notice icon</Label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="option in PROJECT_NOTICE_ICON_OPTIONS"
                :key="option.value"
                type="button"
                class="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors"
                :class="icon === option.value ? 'border-foreground/20 bg-muted text-foreground' : 'border-border/70 bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground'"
                @click="icon = option.value"
              >
                <component :is="getProjectNoticeIconComponent(option.value)" class="size-4 shrink-0" />
                <span>{{ option.label }}</span>
              </button>
            </div>
          </div>

          <div class="space-y-2">
            <Label for="project-notice-color">Notice colour</Label>
            <Input id="project-notice-color" v-model="color" type="color" class="h-10 w-16 p-1" />
          </div>
        </div>
      </div>

      <SheetFooter class="flex flex-wrap justify-between gap-2 sm:justify-between">
        <Button type="button" variant="outline" :disabled="saving" @click="clearNotice">
          Clear notice
        </Button>

        <div class="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" :disabled="saving" @click="closeDialog">
            Cancel
          </Button>
          <Button type="button" :disabled="saving" @click="saveNotice">
            {{ saving ? 'Saving...' : 'Save notice' }}
          </Button>
        </div>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
